import React, { useEffect, useState } from 'react';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import useFetch from 'use-http';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment-timezone';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { InfoButton } from '@/components/ui/shared/InfoButton';
import { Badge } from '@/components/ui/badge';

const VILNIUS_TIMEZONE = 'Europe/Vilnius';

export default function PrescriptionTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);
  const [loading, setLoading] = useState(true);

  const { get: getPrescriptions, response: prescriptionsResponse } = useFetch(
    '/UserAccount/PrescriptionList',
  );
  const { get: getPrescription } = useFetch('/UserAccount/Prescription');

  const isValidVilniusDate = (date) => {
    if (!date) {
      return false;
    }

    return moment(date).tz(VILNIUS_TIMEZONE).isValid();
  };

  const isPrescriptionValid = (effectiveEndDate) => {
    if (!isValidVilniusDate(effectiveEndDate)) {
      return undefined;
    }

    return moment(effectiveEndDate)
      .tz(VILNIUS_TIMEZONE)
      .isAfter(moment().tz(VILNIUS_TIMEZONE));
  };

  const fetchData = async (date = dateRange, searchTerm = search) => {
    setLoading(true);

    sessionDispatch({
      type: 'UPDATE',
      data: { dateFilter: date },
    });

    const query = getQueryString(
      date,
      searchTerm,
      undefined,
      undefined,
      userState.espbiEsi,
    );

    const result = await getPrescriptions(query);

    if (prescriptionsResponse.ok) {
      const sorted = _.orderBy(
        result,
        [(item) => moment(item.dateAsserted).tz(VILNIUS_TIMEZONE).valueOf()],
        ['desc'],
      );

      const mapped = sorted.map((item) => ({
        id: item.id,
        title: item.name,
        date: item.dateAsserted,
        valid: isPrescriptionValid(item.effectiveEndDate),
      }));

      setData(mapped);
    }

    setLoading(false);
  };

  const handleSelection = async (id) => {
    setLoading(true);

    const query = userState.espbiEsi
      ? `?id=${id}&espbiEsi=${userState.espbiEsi}`
      : `?id=${id}`;

    const res = await getPrescription(query);

    history.push('/userAccount/prescriptions/details', { data: res });
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, userState?.espbiEsi]);

  return (
    <DataTable
      title="Receptai"
      columns={columns({ handleSelection })}
      data={data}
      filterableColumns={['title', 'date']}
      loading={loading}
      onRowClick={(row) => handleSelection(row.id)}
    />
  );
}

const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatVilniusDate = (date) => {
  if (!date) {
    return '-';
  }

  const vilniusDate = moment(date).tz(VILNIUS_TIMEZONE);

  if (!vilniusDate.isValid()) {
    return '-';
  }

  return vilniusDate.format('YYYY-MM-DD');
};

const columns = ({ handleSelection }) => [
  {
    accessorKey: 'title',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="RECEPTAS" />
    ),
    cell: ({ row }) => (
      <span className="font-medium max-w-[500px] truncate ">
        {capitalizeFirstLetter(row.original.title)}
      </span>
    ),
  },
  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {formatVilniusDate(row.original.date)}
      </span>
    ),
  },
  {
    accessorKey: 'valid',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="GALIOJIMAS" />
    ),
    cell: ({ row }) => {
      const valid = row.original.valid;

      const variant =
        valid === true ? 'success' : valid === false ? 'fail' : 'neutral';

      const label =
        valid === true
          ? 'Galioja'
          : valid === false
            ? 'Negalioja'
            : 'Nežinomas';

      return <Badge variant={variant}>{label}</Badge>;
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex gap-2 justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelection(row.original.id);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];
